import React, { useState } from 'react';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

export const TypeIcon = ({ type }) => {
  if (type === 'Work') return <WorkOutlineOutlinedIcon style={{ fontSize: 16 }} />;
  if (type === 'Other') return <LocationOnOutlinedIcon style={{ fontSize: 16 }} />;
  return <HomeOutlinedIcon style={{ fontSize: 16 }} />;
};

const AddressCard = ({ address, onEdit, onDelete, onMakeDefault }) => {
  const [expanded, setExpanded] = useState(false);

  const fullAddress = [
    address.address || address.street,
    address.city,
    address.state,
    address.pincode,
  ].filter(Boolean).join(', ');

  const isLong = fullAddress.length > 80;
  const isDefault = Boolean(address.is_default);

  return (
    <div
      className={`group relative flex flex-col rounded-2xl border p-4 sm:p-5 shadow-sm transition-all duration-200 font-sans ${
        isDefault
          ? 'border-emerald-300 ring-2 ring-emerald-500/10 bg-gradient-to-b from-emerald-50/25 to-white'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {/* ── Top Header Row: Address Type badge + Default indicator ── */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-xl ${
              isDefault
                ? 'bg-[#046a38] text-white shadow-xs'
                : 'bg-emerald-50 text-[#046a38]'
            }`}
          >
            <TypeIcon type={address.address_type || address.type} />
          </span>
          <span className="text-xs sm:text-sm font-bold text-gray-900">
            {address.address_type || address.type || 'Home'}
          </span>
        </div>

        {isDefault ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100/80 px-2.5 py-0.5 text-[10px] sm:text-[11px] font-bold text-[#046a38] border border-emerald-200">
            <StarOutlinedIcon style={{ fontSize: 12 }} />
            <span>Default</span>
          </span>
        ) : onMakeDefault ? (
          <button
            type="button"
            onClick={() => onMakeDefault(address.id || address.address_id)}
            className="text-[11px] font-bold text-[#046a38] hover:underline"
          >
            Set as default
          </button>
        ) : null}
      </div>

      {/* ── Recipient Details & Address ── */}
      <div className="mb-4 flex-1 space-y-1.5 text-xs sm:text-sm text-gray-600">
        {address.name && (
          <div className="flex items-center gap-1.5 text-gray-900 font-bold">
            <PersonOutlineOutlinedIcon style={{ fontSize: 15 }} className="text-gray-400 shrink-0" />
            <span className="truncate">{address.name}</span>
          </div>
        )}

        {(address.mobile || address.phone) && (
          <div className="flex items-center gap-1.5 text-gray-600 font-medium">
            <PhoneOutlinedIcon style={{ fontSize: 14 }} className="text-gray-400 shrink-0" />
            <a
              href={`tel:${address.mobile || address.phone}`}
              className="text-xs hover:text-[#046a38] transition font-semibold"
            >
              {address.mobile || address.phone}
            </a>
          </div>
        )}

        <div className="mt-2 pt-2 border-t border-gray-100">
          <p
            className={`leading-relaxed break-words text-gray-700 text-xs sm:text-sm ${
              !expanded && isLong ? 'line-clamp-2' : ''
            }`}
          >
            {address.address || address.street}
          </p>

          <p className="mt-0.5 text-xs font-semibold text-gray-500">
            {[address.city, address.state].filter(Boolean).join(', ')}
            {address.pincode ? ` — ${address.pincode}` : ''}
          </p>
          {(address.gstin || address.gst_number) && (
            <p className="mt-1 text-[11px] font-medium text-gray-500">
              GSTIN: {address.gstin || address.gst_number}
            </p>
          )}

          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((p) => !p)}
              className="mt-1 text-[11px] font-bold text-[#046a38] hover:underline"
            >
              {expanded ? 'Show less' : 'Read full address'}
            </button>
          )}
        </div>
      </div>

      {/* ── Action Buttons Toolbar ── */}
      <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
        {!isDefault && onMakeDefault && (
          <button
            type="button"
            onClick={() => onMakeDefault(address.id || address.address_id)}
            className="flex flex-1 items-center justify-center gap-1 rounded-xl bg-emerald-50 py-2.5 text-xs font-bold text-[#046a38] border border-emerald-200/60 hover:bg-emerald-100 active:scale-95 transition"
          >
            <StarOutlinedIcon style={{ fontSize: 13 }} />
            <span>Make Default</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => onEdit(address)}
          className="flex flex-1 items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white py-2.5 text-xs font-bold text-gray-700 hover:bg-gray-50 active:scale-95 transition"
        >
          <EditOutlinedIcon style={{ fontSize: 14 }} className="text-gray-500" />
          <span>Edit</span>
        </button>

        <button
          type="button"
          onClick={() => onDelete(address.id || address.address_id)}
          className="flex items-center justify-center rounded-xl border border-red-100 bg-red-50/60 p-2.5 text-xs font-bold text-red-600 hover:bg-red-100 active:scale-95 transition"
          title="Delete Address"
        >
          <DeleteOutlineOutlinedIcon style={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
  );
};

export default AddressCard;
